/**
 * Admin notification / sidebar item → in-app route.
 * Covers operational list rows and persisted push log (`fromPushLog` / `adminNotificationId`).
 * @param {Record<string, unknown>} n
 * @returns {string}
 */

function isLikelyMongoId(value) {
  const s = String(value ?? "").trim();
  return /^[a-f0-9]{24}$/i.test(s);
}

export function getAdminNotificationPath(n) {
  const type = String(n?.type || "").toLowerCase();
  const patientId = String(n?.patientId ?? "").trim();
  const doctorId = String(n?.doctorId ?? "").trim();
  const hospitalId = String(n?.hospitalId ?? "").trim();
  const matchRequestId = String(n?.matchRequestId ?? "").trim();
  const appointmentId = String(n?.appointmentId ?? "").trim();
  const prescriptionId = String(n?.prescriptionId ?? "").trim();
  const bidLogId = String(n?.bidLogId ?? n?.bidId ?? "").trim();

  if (type === "pending_signup") return "/review-approval";

  if (type === "doctor_signup" && doctorId) {
    return `/doctor-list/${encodeURIComponent(doctorId)}`;
  }
  if (type === "hospital_signup" && hospitalId) {
    return `/hospital-list/${encodeURIComponent(hospitalId)}`;
  }
  if (type === "doctor_pending_review" && doctorId) {
    return `/doctor-list/${encodeURIComponent(doctorId)}`;
  }
  if (type === "hospital_pending_review" && hospitalId) {
    return `/hospital-list/${encodeURIComponent(hospitalId)}`;
  }

  if (type === "prescription_pending") {
    const tabRaw = String(n?.clinicalTab ?? n?.tab ?? "").toLowerCase();
    const tab = ["prescription", "report", "insurance"].includes(tabRaw) ? tabRaw : "";
    if (prescriptionId) {
      const qs = new URLSearchParams();
      qs.set("prescriptionId", prescriptionId);
      qs.set("returnTo", "clinical");
      if (tab) qs.set("tab", tab);
      return `/patient-medical-submissions?${qs.toString()}`;
    }
    if (matchRequestId) {
      const qs = new URLSearchParams();
      qs.set("highlight", `matchRequest_${matchRequestId}`);
      if (tab) qs.set("tab", tab);
      return `/prescriptions-reports?${qs.toString()}`;
    }
    return "/prescriptions-reports";
  }

  if (
    (type === "prescription_upload" ||
      type === "prescription_resubmitted" ||
      type === "prescription_rejected" ||
      type === "prescription_accepted") &&
    prescriptionId
  ) {
    const tabRaw = String(n?.clinicalTab ?? n?.tab ?? "").toLowerCase();
    const tab = ["prescription", "report", "insurance"].includes(tabRaw) ? tabRaw : "";
    const qs = new URLSearchParams();
    qs.set("prescriptionId", prescriptionId);
    qs.set("returnTo", "clinical");
    if (tab) qs.set("tab", tab);
    return `/patient-medical-submissions?${qs.toString()}`;
  }
  if (type === "prescription_upload" && matchRequestId) {
    const tabRaw = String(n?.clinicalTab ?? n?.tab ?? "").toLowerCase();
    const tab = ["prescription", "report", "insurance"].includes(tabRaw) ? tabRaw : "";
    const qs = new URLSearchParams();
    qs.set("highlight", `matchRequest_${matchRequestId}`);
    if (tab) qs.set("tab", tab);
    return `/prescriptions-reports?${qs.toString()}`;
  }
  if (type === "prescription_upload" && patientId) {
    return `/patient-list/${encodeURIComponent(patientId)}`;
  }

  /** Bid pipeline alerts → Bids console with deep-link params (handled in `Bids.jsx`). */
  const bidEscalationTypes = new Set([
    "no_doctor_responded",
    "bid_expired",
    "expiring_soon",
    "doctor_rejected_after_acceptance",
    "pending_approval",
  ]);
  if (bidEscalationTypes.has(type)) {
    const qs = new URLSearchParams();
    if (isLikelyMongoId(patientId)) qs.set("patientId", patientId);
    if (isLikelyMongoId(matchRequestId)) qs.set("matchRequestId", matchRequestId);
    if (isLikelyMongoId(bidLogId)) qs.set("bidLogId", bidLogId);
    const q = qs.toString();
    if (q) return `/Bid?${q}`;
  }

  const appointmentLike =
    type.includes("appointment") ||
    type === "appointment_booked" ||
    type === "appointment_pending_admin" ||
    type === "appointment_reschedule_pending_admin" ||
    type === "appointment_completed" ||
    type === "appointment_confirmed" ||
    type === "appointment_admin_approved";
  if (appointmentLike) {
    if (patientId) return `/patient-list/${encodeURIComponent(patientId)}`;
    if (appointmentId || bidLogId || matchRequestId) return "/Bid";
    return "/Bid";
  }

  const bidLike =
    type === "reverse_bid" ||
    type === "offer_accepted" ||
    type === "new_bid" ||
    type === "bid_update" ||
    type === "match_request_live";
  if (bidLike) {
    if (patientId) return `/patient-list/${encodeURIComponent(patientId)}`;
    return "/Bid";
  }

  if (type === "patient_account_approved" || type === "patient_account_rejected") {
    if (patientId) return `/patient-list/${encodeURIComponent(patientId)}`;
    return "/patient-list";
  }

  if (patientId) return `/patient-list/${encodeURIComponent(patientId)}`;

  return "/notifications";
}

/** False only when the resolved route is the generic notifications screen (nothing more specific). */
export function isAdminNotificationInteractive(n) {
  return getAdminNotificationPath(n) !== "/notifications";
}
